
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Edit2, Users, Printer } from 'lucide-react';
import { api, normalizeForComparison } from '../services/api';
import { UnionStatementBatch, UnionStatementRecord, BankDeposit, Client } from '../types';
import ConfirmDialog from './modals/ConfirmDialog';
import UnionRecordModal from './modals/UnionRecordModal';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { getDb } from '../lib/firebase';

const UnionDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const [batch, setBatch] = useState<UnionStatementBatch | null>(null);
  const [records, setRecords] = useState<UnionStatementRecord[]>([]);
  const [allClients, setAllClients] = useState<Client[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [unionDepositDate, setUnionDepositDate] = useState<string>(''); // 組合からの入金日
  
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState<UnionStatementRecord | null>(null);

  const [confirmDialog, setConfirmDialog] = useState<any>({ isOpen: false });

  // 入金日の保存エラー表示（成功時は無音、失敗時のみエラー表示）
  const [depositDateError, setDepositDateError] = useState<string>('');

  const fetchData = async () => {
        if (!id) return;
        setIsLoading(true);
        try {
            const batches = await api.union.getBatches();
            const foundBatch = batches.find(b => b.id === id);
            if (foundBatch) {
                setBatch(foundBatch);
                const recs = await api.union.getRecords(id);
                setRecords(recs || []);
                // 編集モーダルでの得意先選択用にマスタを取得（初回のみ）
                if (allClients.length === 0) {
                    try { setAllClients(await api.clients.getAll()); } catch (e) { console.error('Failed to load clients', e); }
                }

                // 入金日の決定ロジック：
                // ①保存済みの deposit_date があれば、それを表示（手入力された値を優先）
                // ②なければ、銀行データから自動推測（既存ロジック・後方互換のため残す）
                if (foundBatch.deposit_date) {
                    setUnionDepositDate(foundBatch.deposit_date);
                    return;
                }

                const db = getDb();
                const rawMonth = foundBatch.year_month || "";
                const formattedMonth = rawMonth.includes('-') ? rawMonth.replace('-', '/') : rawMonth;

                // 組合からの入金日を自動推測（銀行データから検索）
                if (formattedMonth) {
                  const [y, m] = formattedMonth.split('/').map(Number);
                  // 検索範囲: 対象月の1日 ～ 翌々月の末日 (入金サイトを考慮して広めに検索)
                  const startDate = `${y}/${String(m).padStart(2, '0')}/01`;
                  
                  // 翌々月の末日を計算
                  const endDateObj = new Date(y, m + 2, 0); // m+2は翌々月の翌月相当の0日目 -> 翌々月末日
                  const eY = endDateObj.getFullYear();
                  const eM = endDateObj.getMonth() + 1;
                  const eD = endDateObj.getDate();
                  const endDate = `${eY}/${String(eM).padStart(2, '0')}/${String(eD).padStart(2, '0')}`;
                  
                  const dQuery = query(
                      collection(db, 'bank_deposits'), 
                      where('deposit_date', '>=', startDate),
                      where('deposit_date', '<=', endDate)
                  );
                  const dSnap = await getDocs(dQuery);
                  let foundDate = '-';
                  
                  const unionNamePart = foundBatch.union_type === 'satsuma' ? 'サツマ' : 'ミナミサツマ';
                  const normUnionName = normalizeForComparison(unionNamePart);
                  const totalDeposit = foundBatch.total_deposit;

                  const candidates: BankDeposit[] = [];
                  dSnap.forEach(doc => candidates.push(doc.data() as BankDeposit));

                  // 優先順位1: 金額が完全一致するもの
                  const amountMatches = candidates.filter(d => d.amount === totalDeposit);
                  
                  if (amountMatches.length > 0) {
                      // 金額一致が複数ある場合、名称で絞り込み
                      const nameMatch = amountMatches.find(d => 
                          normalizeForComparison(d.remitter_name).includes(normUnionName)
                      );
                      if (nameMatch) {
                          foundDate = nameMatch.deposit_date;
                      } else {
                          // 名称一致がなければ、日付が一番新しいものを採用（あるいは古いもの？）
                          // 候補の中で最も古い日付（＝明細月に近い日付）を採用
                          amountMatches.sort((a, b) => a.deposit_date.localeCompare(b.deposit_date));
                          foundDate = amountMatches[0].deposit_date;
                      }
                  } else {
                      // 優先順位2: 金額不一致だが名称が一致するもの（手数料差引などを考慮）
                      const nameMatches = candidates.filter(d => 
                          normalizeForComparison(d.remitter_name).includes(normUnionName)
                      );
                      
                      if (nameMatches.length > 0) {
                          // 複数あれば金額が近いもの、あるいは日付が近いものを...今回は日付順で先頭
                          nameMatches.sort((a, b) => a.deposit_date.localeCompare(b.deposit_date));
                          foundDate = nameMatches[0].deposit_date;
                      }
                  }
                  
                  setUnionDepositDate(foundDate);
                }
            }
        } catch (e) {
            console.error("Fetch data error:", e);
        } finally {
            setIsLoading(false);
        }
  };

  useEffect(() => { fetchData(); }, [id]);

  const displayRecords = useMemo(() => {
    return records.map(record => ({
        ...record,
        display_billing_amount: record.billing_amount,
        display_difference: record.billing_amount - record.deposit_amount,
    }));
  }, [records]);

  const totalBilling = displayRecords.reduce((s, r) => s + r.display_billing_amount, 0);
  const totalDeposit = displayRecords.reduce((s, r) => s + r.deposit_amount, 0);
  const totalDifference = displayRecords.reduce((s, r) => s + r.display_difference, 0);

  // 不整合検知と自動修正 (Self-Healing)
  useEffect(() => {
      if (!batch || isLoading) return;
      
      const hasBillingDiff = batch.total_billing !== totalBilling;
      const hasDepositDiff = batch.total_deposit !== totalDeposit;

      if (hasBillingDiff || hasDepositDiff) {
          const update = async () => {
              try {
                  await api.union.updateBatch(batch.id, {
                      total_billing: totalBilling,
                      total_deposit: totalDeposit
                  });
                  setBatch(prev => prev ? ({ ...prev, total_billing: totalBilling, total_deposit: totalDeposit }) : null);
                  console.log('Batch totals auto-corrected.', { 
                      old: { b: batch.total_billing, d: batch.total_deposit }, 
                      new: { b: totalBilling, d: totalDeposit } 
                  });
              } catch (e) {
                  console.error('Failed to auto-update batch totals', e);
              }
          };
          update();
      }
  }, [batch, totalBilling, totalDeposit, isLoading]);

  const safeFormat = (val: any) => {
    const num = Number(val || 0);
    return num.toLocaleString();
  };

  const handleDeleteSelected = () => {
      if(selectedIds.size === 0) return;
      setConfirmDialog({
          isOpen: true, title: '削除確認', message: `選択した ${selectedIds.size} 件を削除します。`, type: 'danger', 
          onConfirm: async () => {
              setConfirmDialog({ isOpen: false });
              try { setIsLoading(true); await api.union.deleteRecords(Array.from(selectedIds)); setSelectedIds(new Set()); await fetchData(); } finally { setIsLoading(false); }
          }
      });
  };

  // 入金日を保存（フォーカスアウト時に呼ばれる）
  // 通常時はサイレント保存。失敗時のみエラーメッセージを表示する。
  const handleDepositDateBlur = async () => {
      if (!batch || !id) return;
      // 既存の保存値と同じなら何もしない（不要な書き込みを避ける）
      if ((batch.deposit_date || '') === unionDepositDate) {
          return;
      }
      try {
          await api.union.updateBatch(id, { deposit_date: unionDepositDate || '' });
          setBatch(prev => prev ? ({ ...prev, deposit_date: unionDepositDate }) : null);
          // 成功時はエラー表示をクリア（過去のエラーが残っていれば消す）
          setDepositDateError('');
      } catch (e) {
          console.error('入金日の保存に失敗:', e);
          setDepositDateError('入金日の保存に失敗しました。再度お試しください。');
      }
  };

  const handleEdit = (record: UnionStatementRecord) => { setEditingRecord(record); setIsEditModalOpen(true); };
  
  const handleSaveRecord = async (updated: UnionStatementRecord) => { 
      try { 
          await api.union.saveRecord(updated); 
          await fetchData();
          setIsEditModalOpen(false); 
      } catch(e) { console.error(e); } 
  };

  const handleBrowserPrint = () => {
      window.print();
  };

  if (isLoading) return <div className="p-20 text-center font-black text-gray-400 flex flex-col gap-4">
      <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto"></div>
      データを同期中...
  </div>;

  if (!batch) return <div className="p-20 text-center font-black text-gray-400">データが見つかりません</div>;

  const displayYM = (batch.year_month || "").includes('-') ? batch.year_month.replace('-', '/') : batch.year_month;
  const unionTitleName = batch.union_type === 'satsuma' ? '薩摩砕石協同組合振込明細' : '南さつまリサイクル協同組合振込明細';

  return (
    <div className="space-y-6">
      <div className="hidden print:block mb-4">
        <div className="text-center pt-2 pb-2">
            <h1 className="text-xl font-bold border-b-2 border-black inline-block px-12 pb-1 text-black">
            {unionTitleName}_{displayYM}出力
            </h1>
        </div>
        <div className="text-right text-[10px] font-bold text-black mt-2 pr-4">
            入金日：{unionDepositDate}
        </div>
      </div>

      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 print:hidden">
          <div>
            <button onClick={() => navigate('/union')} className="flex items-center gap-2 text-gray-500 hover:text-blue-600 mb-2 text-sm font-black transition-colors">
                <ArrowLeft size={16} /> 組合管理へ戻る
            </button>
            <h2 className="text-2xl font-black text-gray-800 flex items-center gap-3">
                <Users size={24} className={batch.union_type === 'satsuma' ? 'text-purple-600' : 'text-blue-600'} />
                {batch.union_type === 'satsuma' ? '薩摩砕石協同組合' : '南さつまリサイクル協同組合'}
                <span className="text-gray-400 font-bold ml-1">{displayYM}</span>
            </h2>
            <div className="flex items-center gap-2 mt-2 ml-1">
                <label className="text-xs font-black text-gray-500">入金日：</label>
                <input
                    type="date"
                    value={unionDepositDate && unionDepositDate !== '-' ? unionDepositDate.replace(/\//g, '-') : ''}
                    onChange={(e) => {
                        // YYYY-MM-DD → YYYY/MM/DD に変換して保持
                        const v = e.target.value;
                        setUnionDepositDate(v ? v.replace(/-/g, '/') : '');
                    }}
                    onBlur={handleDepositDateBlur}
                    className="text-xs font-bold text-gray-700 px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 outline-none"
                    aria-label="入金日"
                />
                {depositDateError && (
                    <span className="text-[10px] text-red-600 font-bold">⚠ {depositDateError}</span>
                )}
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
              {selectedIds.size > 0 && (
                <button onClick={handleDeleteSelected} className="px-4 py-2 bg-red-600 text-white rounded font-black text-[13px] shadow">選択分削除（{selectedIds.size}）</button>
              )}
              <button onClick={handleBrowserPrint} className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded font-black text-[13px] shadow-sm hover:bg-gray-50 flex items-center gap-1.5">
                  <Printer size={16}/> 出力（PDF）
              </button>
          </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden print:border-none print:shadow-none">
        <div className="overflow-auto print:overflow-visible">
          <table className="w-full text-sm text-left border-separate border-spacing-0 print:border-collapse print:border-spacing-0 print:w-full">
            <thead className="bg-gray-50 text-gray-600 font-black print:text-black">
              <tr>
                <th className="p-3 w-10 text-center border-b sticky top-0 bg-gray-50 z-10 print:hidden">
                   <input type="checkbox" checked={displayRecords.length > 0 && selectedIds.size === displayRecords.length} onChange={(e) => { if(e.target.checked) setSelectedIds(new Set(displayRecords.map(r => r.id))); else setSelectedIds(new Set()); }} className="w-4 h-4 rounded border-gray-300" />
                </th>
                <th className="p-3 border-b sticky top-0 bg-gray-50 z-10 print:bg-white print:border print:border-black w-[50px] print:w-[60px]">
                    <div className="flex items-center justify-center">得意先CD</div>
                </th>
                <th className="p-3 border-b sticky top-0 bg-gray-50 z-10 print:bg-white print:border print:border-black w-[320px] print:w-[140px]">
                    <div className="flex items-center justify-start print:justify-center">得意先名</div>
                </th>
                <th className="p-3 border-b sticky top-0 bg-gray-50 z-10 print:bg-white print:border print:border-black w-[85px] print:w-[70px]">
                    <div className="flex items-center justify-end print:justify-center">請求額<span className="text-[10px] font-normal block print:hidden">（前月）</span></div>
                </th>
                <th className="p-3 border-b sticky top-0 bg-gray-50 z-10 print:bg-white print:border print:border-black w-[85px] print:w-[70px]">
                    <div className="flex items-center justify-end print:justify-center">振込額</div>
                </th>
                <th className="p-3 border-b sticky top-0 bg-gray-50 z-10 print:bg-white print:border print:border-black w-[85px] print:w-[70px]">
                    <div className="flex items-center justify-end print:justify-center">差額</div>
                </th>
                <th className="p-3 border-b sticky top-0 bg-gray-50 z-10 print:bg-white print:border print:border-black w-[100px] print:w-[50px]">
                    <div className="flex items-center justify-center whitespace-nowrap">区分</div>
                </th>
                <th className="p-3 border-b sticky top-0 bg-gray-50 z-10 print:bg-white print:border print:border-black w-[100px] print:w-[50px]">
                    <div className="flex items-center justify-center whitespace-nowrap">出荷</div>
                </th>
                <th className="p-3 border-b sticky top-0 bg-gray-50 z-10 print:bg-white print:border print:border-black min-w-[100px] print:w-auto">
                    <div className="flex items-center justify-start print:justify-center">備考</div>
                </th>
                <th className="p-3 text-center border-b sticky top-0 bg-gray-50 z-10 print:hidden">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 print:divide-black">
              {displayRecords.map((record) => {
                return (
                  <tr key={record.id} className={`hover:bg-blue-50/10 transition-colors ${selectedIds.has(record.id) ? 'bg-blue-50/50' : ''} print:hover:bg-transparent`}>
                    <td className="p-3 text-center border-b print:hidden">
                       <input type="checkbox" checked={selectedIds.has(record.id)} onChange={() => { const next = new Set(selectedIds); if(next.has(record.id)) next.delete(record.id); else next.add(record.id); setSelectedIds(next); }} className="w-4 h-4 rounded border-gray-300" />
                    </td>
                    <td className="p-3 font-mono font-bold text-gray-800 border-b print:border print:border-black print:text-black text-center">
                        {record.client_code === '未特定' ? <span className="text-red-500 italic print:text-black">未特定</span> : record.client_code}
                    </td>
                    <td className="p-3 font-black text-gray-800 border-b print:border print:border-black print:text-black print:font-normal">
                        <div className="flex flex-col">
                            {record.client_name}
                            {record.client_abbreviation && record.client_abbreviation !== record.client_name && (
                                <span className="text-[10px] text-gray-400 font-bold print:hidden">ソース：{record.client_abbreviation}</span>
                            )}
                        </div>
                    </td>
                    <td className="p-3 text-right font-black border-b print:border print:border-black print:text-black print:font-normal text-gray-800">
                        ¥{safeFormat(record.display_billing_amount)}
                    </td>
                    <td className="p-3 text-right font-black border-b text-gray-800 print:border print:border-black print:text-black print:font-normal">¥{safeFormat(record.deposit_amount)}</td>
                    <td className={`p-3 text-right font-black border-b print:border print:border-black print:text-black print:font-normal ${record.display_difference === 0 ? 'text-gray-300 print:text-black' : 'text-red-500 print:text-black font-bold'}`}>
                        {record.display_difference === 0 ? '0' : `¥${safeFormat(record.display_difference)}`}
                    </td>
                    <td className="p-3 text-gray-500 border-b font-medium print:border print:border-black print:text-black text-center">
                        {record.category || '-'}
                    </td>
                    <td className="p-3 text-gray-500 border-b font-medium print:border print:border-black print:text-black text-center">
                        {record.shipping || '-'}
                    </td>
                    <td className="p-3 border-b print:border print:border-black print:text-black">
                        <span className="text-[10px]">{record.notes || ''}</span>
                    </td>
                    <td className="p-3 text-center border-b print:hidden">
                       <div className="flex justify-center gap-1">
                           <button onClick={() => handleEdit(record)} className="p-1 text-blue-600 hover:bg-blue-50 rounded" title="編集"><Edit2 size={16}/></button>
                       </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot className="bg-gray-50 font-black text-gray-800 print:bg-white print:text-black">
                <tr className="print-total-row">
                    <td className="print:hidden"></td>
                    <td colSpan={2} className="p-4 text-right border-t border-b print:border print:border-black font-bold">合計</td>
                    <td className="p-4 text-right border-t border-b print:border print:border-black print:font-bold">¥{safeFormat(totalBilling)}</td>
                    <td className="p-4 text-right border-t border-b print:border print:border-black print:font-bold">¥{safeFormat(totalDeposit)}</td>
                    <td className="p-4 text-right border-t border-b print:border print:border-black print:font-bold">¥{safeFormat(totalDifference)}</td>
                    <td colSpan={3} className="p-4 border-t border-b print:border print:border-black"></td>
                    <td colSpan={1} className="p-4 border-t print:hidden"></td>
                </tr>
            </tfoot>
          </table>
        </div>
      </div>

      <UnionRecordModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        record={editingRecord}
        onSave={handleSaveRecord}
        allClients={allClients}
      />
      <ConfirmDialog {...confirmDialog} onCancel={() => setConfirmDialog({isOpen: false})} />
    </div>
  );
};

export default UnionDetail;
